import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Utensils, Lock, Mail, Loader2, Eye, EyeOff } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const formatApiError = (detail) => {
  if (detail == null) return "Ocurrió un error. Intenta de nuevo.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => (e && typeof e.msg === "string" ? e.msg : "")).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return "Ocurrió un error. Intenta de nuevo.";
};

export default function Login() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) { toast.error("Ingresa email y contraseña"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email: email.trim(), password });
      if (data?.session_token) localStorage.setItem("session_token", data.session_token);
      setUser(data.user);
      toast.success(`Bienvenido, ${data.user.name || "Admin"}`);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      const msg = formatApiError(err?.response?.data?.detail) || "No se pudo iniciar sesión";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0D12] flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="bg-[#15171E] border border-[#262A33] rounded-2xl p-10 fade-in">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-11 h-11 rounded-xl bg-[#F97316] flex items-center justify-center shadow-lg shadow-orange-900/30">
              <Utensils className="w-6 h-6 text-white" strokeWidth={2} />
            </div>
            <div>
              <div className="font-bold text-[#F5F5F7] text-lg leading-tight" style={{ fontFamily: "Manrope, sans-serif" }}>
                Focus Marketing
              </div>
              <div className="text-xs text-[#9CA3AF]">El Digital de Restaurantes</div>
            </div>
          </div>

          <h1 className="text-3xl font-bold text-[#F5F5F7] mb-2 tracking-tight" style={{ fontFamily: "Manrope, sans-serif" }}>
            Acceso privado
          </h1>
          <p className="text-[#9CA3AF] mb-8 text-sm">
            Inicia sesión con tus credenciales de administrador.
          </p>

          <form onSubmit={submit} className="space-y-4" autoComplete="on">
            <label className="block">
              <span className="block text-xs font-medium text-[#9CA3AF] mb-1.5">Email</span>
              <div className="relative">
                <Mail className="w-4 h-4 text-[#6B7280] absolute left-3 top-1/2 -translate-y-1/2" strokeWidth={1.75}/>
                <input
                  data-testid="login-email-input"
                  autoFocus
                  autoComplete="username"
                  type="email"
                  required
                  value={email}
                  onChange={e=>setEmail(e.target.value)}
                  className="w-full bg-[#0B0D12] border border-[#262A33] rounded-xl pl-9 pr-3 py-2.5 text-sm text-[#F5F5F7] placeholder:text-[#6B7280] focus:outline-none focus:ring-2 focus:ring-[#F97316] focus:border-transparent transition"
                  placeholder="tu@email.com"
                />
              </div>
            </label>

            <label className="block">
              <span className="block text-xs font-medium text-[#9CA3AF] mb-1.5">Contraseña</span>
              <div className="relative">
                <Lock className="w-4 h-4 text-[#6B7280] absolute left-3 top-1/2 -translate-y-1/2" strokeWidth={1.75}/>
                <input
                  data-testid="login-password-input"
                  autoComplete="current-password"
                  type={showPwd ? "text" : "password"}
                  required
                  value={password}
                  onChange={e=>setPassword(e.target.value)}
                  className="w-full bg-[#0B0D12] border border-[#262A33] rounded-xl pl-9 pr-10 py-2.5 text-sm text-[#F5F5F7] placeholder:text-[#6B7280] focus:outline-none focus:ring-2 focus:ring-[#F97316] focus:border-transparent transition"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={()=>setShowPwd(!showPwd)}
                  data-testid="toggle-password-btn"
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 text-[#6B7280] hover:text-[#F5F5F7] transition-colors"
                  aria-label={showPwd ? "Ocultar contraseña" : "Ver contraseña"}
                >
                  {showPwd ? <EyeOff className="w-4 h-4"/> : <Eye className="w-4 h-4"/>}
                </button>
              </div>
            </label>

            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit-btn"
              className="w-full flex items-center justify-center gap-2 bg-[#F97316] hover:bg-[#EA580C] disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold rounded-xl px-4 py-3 transition-colors duration-200"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin"/> : <Lock className="w-4 h-4"/>}
              {loading ? "Ingresando…" : "Ingresar"}
            </button>
          </form>

          <p className="text-xs text-[#6B7280] text-center mt-6">
            Acceso restringido · Focus Marketing
          </p>
        </div>
      </div>
    </div>
  );
}
